import React from 'react';
import { Dialog, Button, Flex, Combobox, TextInput } from '@applyboard/crystal-ui';
import { CourseMappingFormData } from './types';

type CourseMappingDialogFormProps = {
  dialogTitle: string;
  submitButtonText: string;
  initialValues: CourseMappingFormData;
  isBusy: boolean;
  onSubmit: (formData: CourseMappingFormData) => void;
  onCancel: () => void;
  countries: Array<{ label: string; value: string }>;
  educationLevels: Array<{ label: string; value: string }>;
  courses: Array<{ label: string; value: string }>;
};

export function CourseMappingDialogForm(props: CourseMappingDialogFormProps) {
  const {
    dialogTitle,
    submitButtonText,
    initialValues,
    isBusy,
    onSubmit,
    onCancel,
    countries,
    educationLevels,
    courses,
  } = props;

  // Form state
  const [formData, setFormData] = React.useState<CourseMappingFormData>(initialValues);

  const handleSubmit = React.useCallback(() => {
    onSubmit(formData);
  }, [formData, onSubmit]);

  const isFormValid = React.useMemo(() => {
    return formData.sourceCourseName.trim() !== '' && formData.equivalentCourse !== '';
  }, [formData]);

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      size="lg"
    >
      <Dialog.Heading>{dialogTitle}</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={6}>
          {/* Equivalent Course - REQUIRED */}
          <Combobox
            label="Equivalent Course"
            placeholder="Select equivalent course"
            value={formData.equivalentCourse}
            onChange={(value) => setFormData({ ...formData, equivalentCourse: value })}
            required
          >
            {courses.map((course) => (
              <Combobox.Option key={course.value} value={course.value} label={course.label} />
            ))}
          </Combobox>

          {/* Source Course Name - REQUIRED */}
          <TextInput
            label="Source Course Name"
            placeholder="Enter source course name"
            value={formData.sourceCourseName}
            onChange={(value) => setFormData({ ...formData, sourceCourseName: value })}
            required
          />

          {/* Country - OPTIONAL */}
          <Combobox
            label="Country"
            placeholder="Select country"
            value={formData.sourceCountryCode}
            onChange={(value) => setFormData({ ...formData, sourceCountryCode: value })}
          >
            {countries.map((country) => (
              <Combobox.Option key={country.value} value={country.value} label={country.label} />
            ))}
          </Combobox>

          {/* Source Education Level - OPTIONAL */}
          <Combobox
            label="Source Education Level"
            placeholder="Select education level"
            value={formData.sourceEducationLevel}
            onChange={(value) => setFormData({ ...formData, sourceEducationLevel: value })}
          >
            {educationLevels.map((level) => (
              <Combobox.Option key={level.value} value={level.value} label={level.label} />
            ))}
          </Combobox>

          {/* Source Institution Name - OPTIONAL */}
          <TextInput
            label="Source Institution Name"
            placeholder="Enter source institution name"
            value={formData.sourceInstitutionName}
            onChange={(value) => setFormData({ ...formData, sourceInstitutionName: value })}
          />
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill" disabled={isBusy}>
          Cancel
        </Dialog.CloseButton>
        <Button intent="primary" width="fill" onClick={handleSubmit} loading={isBusy} disabled={!isFormValid}>
          {submitButtonText}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
